import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TableModule, TablePageEvent } from 'primeng/table';
import { Button } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { ProgressSpinner } from 'primeng/progressspinner';
import { Tag } from 'primeng/tag';
import { TranslatePipe } from '@ngx-translate/core';

export interface WaitingListEntry {
  waitlist_id: string;
  patient: string;
  patient_info: {
    patient_id: string;
    first_name: string;
    last_name: string;
    email: string;
    phone_primary: string;
    date_of_birth: string;
    country: string;
  };
  desired_check_in: string;
  desired_check_out: string;
  desired_room_type: string | null;
  priority: 1 | 2 | 3 | 4 | 5;
  status: 'actionable' | 'waiting' | 'closed';
  notes: string;
  plan_name: string | null;
  plan_value: number | null;
  created_at: string;
  updated_at: string;
}

@Component({
  selector: 'lib-waiting-list-table',
  standalone: true,
  imports: [
    CommonModule,
    TableModule,
    Button,
    Tooltip,
    ProgressSpinner,
    Tag,
    TranslatePipe,
  ],
  templateUrl: './waiting-list-table.component.html',
  styleUrl: './waiting-list-table.component.css',
})
export class WaitingListTableComponent {
  @Input() waitingList: WaitingListEntry[] = [];
  @Input() loading = false;
  @Input() totalRecords = 0;
  @Input() first = 0;
  @Input() rows = 10;

  @Output() pageChange = new EventEmitter<{ page: number; rows: number }>();
  @Output() edit = new EventEmitter<WaitingListEntry>();
  @Output() delete = new EventEmitter<WaitingListEntry>();
  @Output() convert = new EventEmitter<WaitingListEntry>();

  // Mapa de colores para prioridades 1-5 (estilo timeline)
  priorityColorMap = {
    1: { bgColor: '#ef4444', textColor: '#ffffff', label: 'Muy Alta' }, // Rojo
    2: { bgColor: '#fb923c', textColor: '#ffffff', label: 'Alta' }, // Naranja
    3: { bgColor: '#fbbf24', textColor: '#1f2937', label: 'Media' }, // Amarillo
    4: { bgColor: '#60a5fa', textColor: '#ffffff', label: 'Baja' }, // Azul
    5: { bgColor: '#9ca3af', textColor: '#ffffff', label: 'Muy Baja' }, // Gris
  };

  onEdit(entry: WaitingListEntry) {
    if (entry && entry.waitlist_id) {
      console.log('[WaitingListTable] Editing entry:', entry);
      this.edit.emit(entry);
    }
  }

  onDelete(entry: WaitingListEntry) {
    this.delete.emit(entry);
  }

  onConvert(entry: WaitingListEntry) {
    console.log('[WaitingListTable] Converting to reservation:', entry);
    this.convert.emit(entry);
  }

  getStatusSeverity(status: string): 'success' | 'info' | 'warn' | 'danger' {
    switch (status) {
      case 'actionable':
        return 'success';
      case 'waiting':
        return 'warn';
      case 'closed':
        return 'info';
      default:
        return 'info';
    }
  }

  getPriorityStyles(priority: 1 | 2 | 3 | 4 | 5): { [key: string]: string } {
    const colors = this.priorityColorMap[priority];
    return {
      'background-color': colors.bgColor,
      color: colors.textColor,
    };
  }

  getPriorityLabel(priority: 1 | 2 | 3 | 4 | 5): string {
    return this.priorityColorMap[priority].label;
  }

  onPageChange(event: TablePageEvent) {
    const first = event.first ?? 0;
    const rows = event.rows ?? this.rows;
    const page = first / rows + 1;
    this.pageChange.emit({ page, rows });
  }

  truncateNotes(notes: string, maxLength = 50): string {
    if (!notes) return '';
    return notes.length > maxLength ? notes.slice(0, maxLength) + '...' : notes;
  }
}
